import { useEffect, useRef, useState } from 'react'
import type { KeyboardEvent, MouseEvent, WheelEvent } from 'react'

import {
	applyLinkedInLoginV2Fields,
	linkedInLoginV2Click,
	linkedInLoginV2Document,
	linkedInLoginV2Key,
	linkedInLoginV2Status,
	linkedInLoginV2Wheel,
	closeLinkedInLoginV2,
	startLinkedInLoginV2
} from './api'
import css from './LinkedInLoginV2.module.scss'
import type { LinkedInLoginHeaders, LinkedInLoginProfile } from './types'

type LinkedInLoginSolved = {
	headers: LinkedInLoginHeaders
	profile?: LinkedInLoginProfile | null
	correlationId?: string
}

type LinkedInLoginV2Props = {
	onFailed: (message: string) => void
	onSolved: (solved: LinkedInLoginSolved) => void
}

function createCorrelationId() {
	const result = crypto.randomUUID().split('-').join('')
	return result
}

function LinkedInLoginV2({ onFailed, onSolved }: LinkedInLoginV2Props) {
	const [browserSessionId, setBrowserSessionId] = useState<string | null>(null)
	const [pageMode, setPageMode] = useState('')
	const [pageHtml, setPageHtml] = useState('')
	const [frameUrl, setFrameUrl] = useState<string | null>(null)
	const [starting, setStarting] = useState(true)
	const [working, setWorking] = useState(false)
	const [finishing, setFinishing] = useState(false)
	const [failure, setFailure] = useState<string | null>(null)
	const imageRef = useRef<HTMLImageElement | null>(null)
	const correlationIdRef = useRef(createCorrelationId())
	const solvedRef = useRef(false)
	const onFailedRef = useRef(onFailed)
	const onSolvedRef = useRef(onSolved)
	onFailedRef.current = onFailed
	onSolvedRef.current = onSolved

	useEffect(() => {
		let cancelled = false
		let activeBrowserId: string | null = null
		solvedRef.current = false

		async function openBrowser() {
			try {
				const session = await startLinkedInLoginV2(correlationIdRef.current)
				const serverCorrelationId = session.correlationId || session.CorrelationId || ''
				if (serverCorrelationId) {
					correlationIdRef.current = serverCorrelationId
				}
				const browserSessionId = session.browserSessionId || session.BrowserSessionId || ''
				if (cancelled) {
					if (browserSessionId) {
						await closeLinkedInLoginV2(browserSessionId, correlationIdRef.current)
					}
					return
				}

				if (!browserSessionId) {
					throw new Error('LoginV2 did not return a session id.')
				}

				activeBrowserId = browserSessionId
				setBrowserSessionId(browserSessionId)
				setStarting(false)
			} catch (error) {
				if (!cancelled) {
					const message = errorText(error)
					setFailure(message)
					setStarting(false)
					onFailedRef.current(message)
				}
			}
		}

		void openBrowser()
		return () => {
			cancelled = true
			if (activeBrowserId && !solvedRef.current) {
				void closeLinkedInLoginV2(activeBrowserId, correlationIdRef.current)
			}
		}
	}, [])

	useEffect(() => {
		if (!browserSessionId || solvedRef.current) {
			return
		}

		let cancelled = false
		const timer = window.setInterval(() => {
			void refreshDocument()
		}, 500)
		void refreshDocument()

		async function refreshDocument() {
			if (cancelled || solvedRef.current || !browserSessionId) {
				return
			}

			try {
				const nextDocument = await linkedInLoginV2Document(
					browserSessionId,
					correlationIdRef.current
				)
				if (cancelled) {
					if (nextDocument?.url) {
						URL.revokeObjectURL(nextDocument.url)
					}
					return
				}

				if (!nextDocument) {
					return
				}

				if (nextDocument.mode === 'background') {
					setFinishing(true)
					return
				}

				if (nextDocument.mode === 'waiting') {
					setFinishing(false)
					setPageMode('waiting')
					return
				}

				setFinishing(false)
				setPageMode(nextDocument.mode)
				if (nextDocument.mode === 'html') {
					setPageHtml(current => (current === nextDocument.html ? current : nextDocument.html))
					setFrameUrl(current => {
						if (current) {
							URL.revokeObjectURL(current)
						}
						return null
					})
					return
				}

				setPageHtml('')
				if (nextDocument.url) {
					setFrameUrl(current => {
						if (current) {
							URL.revokeObjectURL(current)
						}
						return nextDocument.url
					})
				}
			} catch {
				// The page can be navigating between the captcha and the next checkpoint.
			}
		}

		return () => {
			cancelled = true
			window.clearInterval(timer)
		}
	}, [browserSessionId])

	useEffect(() => {
		if (!browserSessionId) {
			return
		}

		let cancelled = false
		const timer = window.setInterval(() => {
			void pollStatus()
		}, 1500)

		async function pollStatus() {
			if (cancelled || solvedRef.current || !browserSessionId) {
				return
			}

			try {
				const status = await linkedInLoginV2Status(
					browserSessionId,
					correlationIdRef.current
				)
				const statusName = status.status || status.Status || ''
				if (statusName === 'Failed') {
					solvedRef.current = true
					const message = status.message || status.Message || 'Login failed'
					setFailure(message)
					onFailedRef.current(message)
					return
				}

				const headers = status.headers ?? status.Headers ?? null
				if (statusName !== 'Succeeded' || !headers?.cookies?.length) {
					return
				}

				solvedRef.current = true
				const profile = status.profile ?? status.Profile ?? null
				const solved = {
					headers,
					profile,
					correlationId: correlationIdRef.current
				}
				onSolvedRef.current(solved)
			} catch {
				// Keep polling until the browser session is closed.
			}
		}

		return () => {
			cancelled = true
			window.clearInterval(timer)
		}
	}, [browserSessionId])

	useEffect(() => {
		return () => {
			if (frameUrl) {
				URL.revokeObjectURL(frameUrl)
			}
		}
	}, [frameUrl])

	async function handleClick(event: MouseEvent<HTMLImageElement>) {
		if (!browserSessionId || !imageRef.current) {
			return
		}

		const image = imageRef.current
		const rect = image.getBoundingClientRect()
		const width = image.naturalWidth > 0 ? image.naturalWidth : rect.width
		const height = image.naturalHeight > 0 ? image.naturalHeight : rect.height
		const x = ((event.clientX - rect.left) / rect.width) * width
		const y = ((event.clientY - rect.top) / rect.height) * height
		await linkedInLoginV2Click(browserSessionId, correlationIdRef.current, x, y)
	}

	async function handleKeyDown(event: KeyboardEvent<HTMLImageElement>) {
		if (!browserSessionId) {
			return
		}

		event.preventDefault()
		const shortcut = event.ctrlKey || event.metaKey
		if (shortcut) {
			return
		}

		if (event.key.length === 1) {
			await linkedInLoginV2Key(browserSessionId, correlationIdRef.current, { text: event.key })
			return
		}

		await linkedInLoginV2Key(browserSessionId, correlationIdRef.current, { key: event.key })
	}

	function handleDocumentLoad(frame: HTMLIFrameElement) {
		const doc = frame.contentDocument
		const view = doc?.defaultView
		if (!doc || !view || !browserSessionId) {
			return
		}

		const send = (buttonIndex: number | null, buttonId?: string | null) => {
			setWorking(true)
			const fields = collectFields(doc)
			const pending = applyLinkedInLoginV2Fields(
				browserSessionId,
				correlationIdRef.current,
				fields,
				buttonIndex,
				buttonId
			).finally(() => {
				setWorking(false)
			})
			return pending
		}

		const unlock = (target: EventTarget | null) => {
			if (target instanceof view.HTMLInputElement) {
				target.removeAttribute('readonly')
			}
		}

		doc.addEventListener('pointerdown', event => unlock(event.target), true)
		doc.addEventListener('focusin', event => unlock(event.target))
		doc.addEventListener('submit', event => {
			event.preventDefault()
			void send(null)
		})
		doc.addEventListener('click', event => {
			const target = event.target
			if (!(target instanceof view.Element)) {
				return
			}

			const button = target.closest('button, input[type="submit"]')
			if (!(button instanceof view.HTMLElement)) {
				return
			}

			event.preventDefault()
			if (button.getAttribute('data-snaily-reveal') === 'true') {
				const input = button.parentElement?.querySelector('input')
				if (input instanceof view.HTMLInputElement) {
					const revealing = input.classList.contains('mask')
					input.classList.toggle('mask', !revealing)
					button.classList.toggle('open', revealing)
				}
				return
			}

			const clickId = button.getAttribute('data-snaily-click')
			if (clickId) {
				button.setAttribute('disabled', 'true')
				void send(null, clickId).finally(() => {
					button.removeAttribute('disabled')
				})
				return
			}

			if (button.getAttribute('data-snaily-submit') === 'true') {
				button.setAttribute('disabled', 'true')
				void send(null).finally(() => {
					button.removeAttribute('disabled')
				})
				return
			}

			const buttons = Array.from(
				doc.querySelectorAll('button, input[type="submit"]')
			).filter(isShown)
			const index = buttons.indexOf(button)
			void send(index >= 0 ? index : null)
		})
	}

	async function handleWheel(event: WheelEvent<HTMLImageElement>) {
		if (!browserSessionId) {
			return
		}

		event.preventDefault()
		await linkedInLoginV2Wheel(
			browserSessionId,
			correlationIdRef.current,
			event.deltaX,
			event.deltaY
		)
	}

	if (failure) {
		const failed = <p className={css.hint}>{failure}</p>
		return failed
	}

	if (starting) {
		const waiting = <Wait text='Opening LinkedIn…' />
		return waiting
	}

	if (finishing) {
		const settling = <Wait text='Finishing sign-in…' />
		return settling
	}

	if (pageMode === 'waiting') {
		const pending = <Wait text='Waiting for LinkedIn…' />
		return pending
	}

	if (pageMode === 'html' && pageHtml) {
		const page = (
			<div className={css.shell}>
				{working ? <Wait text='Working…' /> : null}
				{pageHtml.includes('data-snaily-app') || working ? null : (
					<p className={css.hint}>Fill in the LinkedIn form below.</p>
				)}
				<iframe
					className={
						pageHtml.includes('data-snaily-app')
							? css.appPage
							: pageHtml.includes('data-snaily-submit')
								? css.formPage
								: css.page
					}
					sandbox='allow-same-origin allow-forms'
					srcDoc={pageHtml}
					title='LinkedIn'
					onLoad={event => handleDocumentLoad(event.currentTarget)}
				/>
			</div>
		)
		return page
	}

	if (!frameUrl) {
		const empty = <Wait text='Waiting for the security check…' />
		return empty
	}

	const result = (
		<div className={css.shell}>
			<p className={css.hint}>
				Sign in to LinkedIn in the window below. It stays open until sign-in finishes.
			</p>
			<img
				ref={imageRef}
				alt='LinkedIn security check'
				className={css.frame}
				src={frameUrl}
				draggable={false}
				tabIndex={0}
				onClick={event => void handleClick(event)}
				onKeyDown={event => void handleKeyDown(event)}
				onWheel={event => void handleWheel(event)}
			/>
		</div>
	)
	return result
}

function Wait({ text }: { text: string }) {
	const result = (
		<p className={css.wait}>
			<span className={css.spinner} />
			{text}
		</p>
	)
	return result
}

function collectFields(doc: Document) {
	const view = doc.defaultView
	const fields: Array<{ name: string; id: string; type: string; value: string }> = []
	if (!view) {
		return fields
	}

	const nodes = Array.from(doc.querySelectorAll('input, textarea, select'))
	for (const node of nodes) {
		if (
			!(
				node instanceof view.HTMLInputElement ||
				node instanceof view.HTMLTextAreaElement ||
				node instanceof view.HTMLSelectElement
			)
		) {
			continue
		}

		const type = (node.getAttribute('type') || '').toLowerCase()
		if (type === 'hidden' || type === 'submit' || type === 'button' || type === 'file') {
			continue
		}

		if (
			(type === 'checkbox' || type === 'radio') &&
			node instanceof view.HTMLInputElement &&
			!node.checked
		) {
			continue
		}

		const value = type === 'checkbox' || type === 'radio' ? 'true' : node.value
		fields.push({
			name: node.getAttribute('name') || '',
			id: node.id || '',
			type,
			value
		})
	}
	return fields
}

function isShown(node: Element) {
	const view = node.ownerDocument.defaultView
	if (!view || !(node instanceof view.HTMLElement)) {
		return false
	}

	const result = node.offsetWidth > 0 || node.offsetHeight > 0
	return result
}

function errorText(error: unknown) {
	const axiosData = (error as { axios?: { response?: { data?: { message?: string } } } })?.axios
		?.response?.data?.message
	if (axiosData) {
		return axiosData
	}

	if (error instanceof Error && error.message) {
		return error.message
	}

	const result = 'Could not open the LinkedIn security check.'
	return result
}

export { LinkedInLoginV2 }
